"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  QuizAnswerState,
  QuizSnapshot,
  GenerateQuizResult,
} from "@/lib/personal-quiz/contract";
import { recommendedDraft, type QuizDraft } from "./quiz-draft";
import { RANDOM_PROBLEMS_KEEP_TTL_MS } from "@workspace/gamja-platform/lib/random-problems-persistence";
import { quizConditionsSchema } from "@/lib/personal-quiz/contract";
type PendingResult = { id: string; problemId: string; result: QuizAnswerState };
type Size = { count: number; theoryCount: number };
export function useQuizSession(userId: string) {
  const [draft, setDraft] = useState(recommendedDraft);
  const [snapshot, setSnapshot] = useState<QuizSnapshot | null>(null);
  const [recent, setRecent] = useState<QuizSnapshot[]>([]);
  const [viewing, setViewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [keep, setKeep] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<
    Record<string, { id: string; problemId: string; result: QuizAnswerState }>
  >({});
  const [notice, setNotice] = useState<{
    title: string;
    message: string;
    action?: { conditions: QuizDraft; size?: Size; label: string; name?: string };
  } | null>(null);
  const [live, setLive] = useState<QuizSnapshot | null>(null);
  const inFlight = useRef(false);
  const pending = useRef<{ key: string; id: string } | null>(null);
  const key = `jcg:personal-quiz:${userId}`;
  const pendingPrefix = `${key}:result:`;
  const keepRef = useRef(false);
  const savedRef = useRef<{
    conditions: QuizDraft;
    name?: string;
    id: string;
    expiresAt: number;
  } | null>(null);
  const mounted = useRef(true);
  const persist = useCallback(() => {
    try {
      if (savedRef.current)
        localStorage.setItem(
          key,
          JSON.stringify({ ...savedRef.current, keep: keepRef.current }),
        );
    } catch {
      /* 개인 모드에서도 현재 풀이는 가능하다. */
    }
  }, [key]);
  const accept = useCallback(
    (next: QuizSnapshot) => {
      setLive(next);
      setSnapshot(next);
      setViewing(false);
      setNotice(null);
      setRecent((old) =>
        [next, ...old.filter((s) => s.id !== next.id)].slice(0, 10),
      );
      savedRef.current = {
        conditions: next.requestedConditions ?? next.conditions,
        name: next.name,
        id: next.id,
        expiresAt: Date.now() + RANDOM_PROBLEMS_KEEP_TTL_MS,
      };
      persist();
    },
    [persist],
  );
  const generate = useCallback(
    async (conditions: QuizDraft, acceptedSize?: Size, name?: string) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      const requestKey = JSON.stringify({ conditions, acceptedSize, name });
      if (pending.current?.key !== requestKey)
        pending.current = { key: requestKey, id: crypto.randomUUID() };
      try {
        const response = await fetch("/api/personal-quiz", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conditions,
            name,
            acceptedSize,
            requestId: pending.current.id,
          }),
        });
        const result: GenerateQuizResult = await response.json();
        if (!mounted.current) return;
        if (!result.ok) {
          if (response.status < 500) pending.current = null;
          if (result.code === "REDUCED_COUNT_CONFIRMATION")
            setNotice({
              title: `${result.proposal.count}문제로 만들까요?`,
              message: result.message,
              action: {
                conditions,
                name,
                size: result.proposal,
                label: `${result.proposal.count}문제로 만들기`,
              },
            });
          else
            setNotice({
              title:
                result.code === "INSUFFICIENT_CANDIDATES"
                  ? "조건에 맞는 문제가 없어요"
                  : "시험지를 만들지 못했어요",
              message: result.message,
              ...(response.status >= 500
                ? {
                    action: {
                      conditions,
                      name,
                      size: acceptedSize,
                      label: "다시 시도",
                    },
                  }
                : {}),
            });
          return;
        }
        pending.current = null;
        accept(result.snapshot);
      } catch {
        if (mounted.current)
          setNotice({
            title: "연결을 확인해 주세요",
            message: "시험지를 불러오지 못했어요.",
            action: { conditions, name, size: acceptedSize, label: "다시 시도" },
          });
      } finally {
        inFlight.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [accept],
  );
  const saveResult = useCallback(
    async (id: string, problemId: string, result: QuizAnswerState) => {
      const errorKey = `${id}:${problemId}`;
      const entry = { id, problemId, result };
      // 요청 전에 보관해야 새로고침/탭 종료 중에도 원래 시험지로 재전송할 수 있다.
      try {
        localStorage.setItem(pendingPrefix + errorKey, JSON.stringify(entry));
      } catch {
        /* 저장소 제한 시 현재 화면 재시도 */
      }
      try {
        const response = await fetch("/api/personal-quiz/sessions", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(entry),
          keepalive: true,
        });
        if (!response.ok) throw new Error("기록 저장 실패");
        try {
          localStorage.removeItem(pendingPrefix + errorKey);
        } catch {
          /* 다음 방문에서 멱등 재시도 */
        }
        if (mounted.current)
          setSaveErrors((old) => {
            const next = { ...old };
            delete next[errorKey];
            return next;
          });
      } catch {
        if (mounted.current)
          setSaveErrors((old) => ({ ...old, [errorKey]: entry }));
      }
    },
    [pendingPrefix],
  );
  const [reload, setReload] = useState(0);
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/personal-quiz/sessions", {
          cache: "no-store",
        });
        if (!response.ok) throw new Error("최근 시험지를 불러오지 못했어요.");
        const data = await response.json();
        if (cancelled) return;
        const recovered: QuizSnapshot[] = data.recent;
        // 사용자별 키만 읽고 보관 중인 세트/문제만 복원한다. 서버 결과가 우선이다.
        try {
          for (const storageKey of Object.keys(localStorage)) {
            if (!storageKey.startsWith(pendingPrefix)) continue;
            try {
              const entry: PendingResult = JSON.parse(
                localStorage.getItem(storageKey) ?? "null",
              );
              const target = recovered.find(
                (s) =>
                  s.id === entry?.id && s.problemIds.includes(entry.problemId),
              );
              if (
                !target ||
                target.results?.[entry.problemId] ||
                !entry.result?.isCorrectAnswerVisible
              ) {
                localStorage.removeItem(storageKey);
                continue;
              }
              target.results = {
                ...target.results,
                [entry.problemId]: entry.result,
              };
              void saveResult(entry.id, entry.problemId, entry.result);
            } catch {
              localStorage.removeItem(storageKey);
            }
          }
        } catch {
          /* 로컬 저장소가 차단되어도 서버 기록은 표시한다. */
        }
        setRecent(recovered);
        let saved: null | {
          conditions: QuizDraft;
          name?: string;
          id: string;
          keep: boolean;
          expiresAt: number;
        } = null;
        try {
          saved = JSON.parse(localStorage.getItem(key) ?? "null");
        } catch {
          /* 저장값 손상 시 새 폼 */
        }
        if (saved && quizConditionsSchema.safeParse(saved.conditions).success) {
          savedRef.current = saved;
          keepRef.current = !!saved.keep;
          setKeep(!!saved.keep);
          setDraft(saved.conditions);
          const prior = data.recent.find(
            (s: QuizSnapshot) => s.id === saved!.id,
          );
          if (
            saved.keep &&
            Number.isFinite(saved.expiresAt) &&
            saved.expiresAt > Date.now() &&
            prior
          ) {
            setLive(prior);
            setSnapshot(prior);
            setViewing(false);
          } else await generate(saved.conditions, undefined, saved.name);
        }
      } catch (e) {
        if (!cancelled)
          setLoadError(
            e instanceof Error ? e.message : "최근 시험지를 불러오지 못했어요.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [key, pendingPrefix, saveResult, generate, reload]);
  const toggleKeep = (next: boolean) => {
    keepRef.current = next;
    setKeep(next);
    if (next && savedRef.current)
      savedRef.current.expiresAt = Date.now() + RANDOM_PROBLEMS_KEEP_TTL_MS;
    persist();
  };
  const open = (next: QuizSnapshot) => {
    if (inFlight.current) return;
    setSnapshot(next);
    setViewing(true);
    setNotice(null);
  };
  const clonePending = useRef<{ id: string; requestId: string } | null>(null);
  const clone = async (prior: QuizSnapshot) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    if (clonePending.current?.id !== prior.id)
      clonePending.current = { id: prior.id, requestId: crypto.randomUUID() };
    try {
      const response = await fetch("/api/personal-quiz/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(clonePending.current),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      if (mounted.current) {
        clonePending.current = null;
        accept(data.snapshot);
      }
    } catch (e) {
      if (mounted.current)
        setNotice({
          title: "시험지를 만들지 못했어요",
          message: e instanceof Error ? e.message : "다시 시도해 주세요.",
        });
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const record = (id: string, problemId: string, result: QuizAnswerState) => {
    const update = (s: QuizSnapshot) =>
      s.id === id
        ? { ...s, results: { ...s.results, [problemId]: result } }
        : s;
    setLive((s) => (s ? update(s) : s));
    setSnapshot((s) => (s ? update(s) : s));
    setRecent((old) => old.map(update));
    void saveResult(id, problemId, result);
  };
  return {
    draft,
    setDraft,
    snapshot,
    recent,
    viewing,
    busy,
    loading,
    keep,
    toggleKeep,
    readKeepRemaining: useCallback(
      () =>
        savedRef.current
          ? Math.max(0, savedRef.current.expiresAt - Date.now())
          : null,
      [],
    ),
    extendKeep: () => {
      if (!savedRef.current) return null;
      savedRef.current.expiresAt = Date.now() + RANDOM_PROBLEMS_KEEP_TTL_MS;
      persist();
      return savedRef.current.expiresAt;
    },
    notice,
    setNotice,
    generate,
    open,
    clone,
    record,
    loadError,
    resume: () => {
      if (live && !inFlight.current) {
        setSnapshot(live);
        setViewing(false);
      }
    },
    startCreating: () => {
      if (inFlight.current) return;
      setSnapshot(null);
      setLive(null);
      setViewing(false);
      setNotice(null);
      savedRef.current = null;
      pending.current = null;
      clonePending.current = null;
      try {
        localStorage.removeItem(key);
      } catch {
        /* 현재 화면 전환은 유지한다. */
      }
    },
    hasLive: !!live,
    retryLoad: () => {
      setLoading(true);
      setLoadError(null);
      setReload((n) => n + 1);
    },
    saveErrors,
    retrySave: () =>
      Object.values(saveErrors).forEach(
        (v) => void saveResult(v.id, v.problemId, v.result),
      ),
  };
}
